import { FiEdit2, FiTrash2 } from "react-icons/fi";
import EditPromptModal from "./EditPromptModal";
import type { Prompt } from "../../../types/PromptTypes";
import { promptCoverSrc } from "../../../utils/smileDesign/promptCoverSrc";

interface Props {
    prompt: Prompt;
    isEditing: boolean;
    submitting?: boolean;

    editName: string;
    editHeading: string;
    editDescription: string;
    editActive: boolean;
    editStlFile: File | null;
    editCoverFile?: File | null;

    setEditName: (v: string) => void;
    setEditHeading: (v: string) => void;
    setEditDescription: (v: string) => void;
    setEditActive: (v: boolean) => void;
    setEditStlFile: (v: File | null) => void;
    setEditCoverFile?: (v: File | null) => void;

    autoResizeTextarea: (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => void;

    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onDelete: () => void;
}

export default function PromptDisplay({
    prompt,
    isEditing,
    submitting = false,
    editName,
    editHeading,
    setEditHeading,
    editDescription,
    editActive,
    editStlFile,
    editCoverFile = null,
    setEditName,
    setEditDescription,
    setEditActive,
    setEditStlFile,
    setEditCoverFile,
    autoResizeTextarea,
    onEdit,
    onSave,
    onCancel,
    onDelete,
}: Props) {
    const description =
        typeof prompt.value === "string" && !prompt.value.trim().startsWith("{")
            ? prompt.value
            : "";

    return (
        <>
            <div className="mb-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="font-semibold text-lg text-white">
                            {prompt.heading || prompt.name}
                        </h2>

                        {prompt.heading && prompt.name !== prompt.heading && (
                            <p className="text-sm text-cyan-400 mt-1">{prompt.name}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            <span
                                className={`px-3 py-1 rounded-full text-xs ${
                                    prompt.active
                                        ? "bg-green-500/10 text-green-400"
                                        : "bg-red-500/10 text-red-400"
                                }`}
                            >
                                {prompt.active ? "Active" : "Inactive"}
                            </span>
                            {prompt.has_stl && (
                                <span className="px-3 py-1 rounded-full text-xs bg-amber-500/10 text-amber-300">
                                    STL ready
                                </span>
                            )}
                            {prompt.stl_original_name && (
                                <span className="px-3 py-1 rounded-full text-xs bg-white/5 text-[#b7bab8]">
                                    {prompt.stl_original_name}
                                </span>
                            )}
                            {prompt.filter_status === "failed" && (
                                <span className="px-3 py-1 rounded-full text-xs bg-red-500/10 text-red-400">
                                    Upload failed
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {(prompt.filter_image || prompt.front_thumb) && (
                    <img
                        key={prompt.filter_image || prompt.front_thumb || prompt.id}
                        src={promptCoverSrc(prompt.filter_image || prompt.front_thumb)}
                        alt={`${prompt.name} cover`}
                        className="mt-4 w-full aspect-video rounded-xl object-contain border border-[#2a2e2c] bg-[#C1C1C3]"
                    />
                )}

                <p className="text-[#b7bab8] whitespace-pre-wrap mt-4 text-sm">
                    {description || "No description"}
                </p>
                {prompt.filter_error && (
                    <p className="text-red-400 text-sm mt-3">{prompt.filter_error}</p>
                )}
            </div>

            <div className="flex gap-3">
                <button
                    onClick={onEdit}
                    className="flex-1 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 py-3 rounded-xl flex items-center justify-center gap-2 transition-all"
                >
                    <FiEdit2 />
                    Edit
                </button>
                <button
                    onClick={onDelete}
                    className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 py-3 rounded-xl flex items-center justify-center gap-2 transition-all"
                >
                    <FiTrash2 />
                    Delete
                </button>
            </div>

            {isEditing && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/80 backdrop-blur-md"
                        onClick={onCancel}
                    />
                    <div className="smile-dark-scroll relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0d0f0e] border border-[#262928] rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8">
                        <EditPromptModal
                            prompt={prompt}
                            editName={editName}
                            editHeading={editHeading}
                            editDescription={editDescription}
                            editActive={editActive}
                            editStlFile={editStlFile}
                            editCoverFile={editCoverFile}
                            submitting={submitting}
                            setEditName={setEditName}
                            setEditHeading={setEditHeading}
                            setEditDescription={setEditDescription}
                            setEditActive={setEditActive}
                            setEditStlFile={setEditStlFile}
                            setEditCoverFile={setEditCoverFile || (() => undefined)}
                            autoResizeTextarea={autoResizeTextarea}
                            onSave={onSave}
                            onCancel={onCancel}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
